
import { useState } from 'react';
import { Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TeacherSubject } from '@/hooks/useTeacherSubjects';

interface SubjectsTableProps {
  subjects: TeacherSubject[];
  onEdit: (subject: TeacherSubject) => void;
  onDelete: (id: string) => Promise<void>;
}

export function SubjectsTable({ subjects, onEdit, onDelete }: SubjectsTableProps) {
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    subjectId?: string;
    subjectName?: string;
  }>({ isOpen: false });

  const handleDeleteClick = (id: string, subjectName: string) => {
    setDeleteDialog({
      isOpen: true,
      subjectId: id,
      subjectName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.subjectId) {
      await onDelete(deleteDialog.subjectId);
    }
    setDeleteDialog({ isOpen: false });
  };

  const getCourseTypeLabel = (courseType: string) => {
    return courseType === 'Regular' ? 'ปกติ' : courseType;
  };

  if (subjects.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        ยังไม่มีข้อมูลวิชาที่สอน
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>วิชา</TableHead>
            <TableHead className="w-[100px]">จัดการ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subjects.map((subject) => {
            const isDeprecatedPrimary = subject.grade_level === "ประถม";
            
            return (
              <TableRow 
                key={subject.id}
                className={isDeprecatedPrimary ? "bg-red-50 hover:bg-red-100" : ""}
              >
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {isDeprecatedPrimary && (
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      )}
                      <span className={`font-medium ${isDeprecatedPrimary ? 'text-red-800' : ''}`}>
                        {subject.subject}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge 
                        variant={isDeprecatedPrimary ? "destructive" : "secondary"} 
                        className="text-xs"
                      >
                        {subject.grade_level}
                        {isDeprecatedPrimary && " (ใช้ไม่ได้)"}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {getCourseTypeLabel(subject.course_type)}
                      </Badge>
                    </div>
                    {isDeprecatedPrimary && (
                      <p className="text-xs text-red-600 font-medium">
                        ⚠️ ระดับนี้จะถูกยกเลิก กรุณาเพิ่มวิชาใหม่เป็น ประถมต้น หรือ ประถมปลาย
                      </p>
                    )}
                  </div>
                </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(subject)}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteClick(subject.id, subject.subject)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog({ isOpen: open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบวิชา "{deleteDialog.subjectName}" ใช่หรือไม่?
              <br />
              <span className="text-red-600 font-medium">การดำเนินการนี้ไม่สามารถยกเลิกได้</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
